/**
 * A one-shot contextual prompt for the shared sign-in modal.
 *
 * The modal is mounted ONCE in `MainLayout` with fixed props, so a flow that
 * opens it via the hash has no prop channel for "why you are seeing this" —
 * e.g. the demo flow's "an account with this email already exists" handoff
 * (PR #1375 nit 11 pivot). Same module-store pattern as
 * `features/waivers/waiverGateHold`.
 *
 * Set the prompt, then open the hash modal; the modal renders the message
 * under its header, seeds its email field, and clears the prompt on close so
 * the next plain open carries no stale context.
 *
 * @module ui/layout/SignInModal/signInPrompt
 */

import { useSyncExternalStore } from 'react';

export interface SignInPrompt {
  /** Contextual line rendered under the modal header. */
  message: string;
  /** Seeds the modal's email field. */
  email?: string;
}

let prompt: SignInPrompt | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): SignInPrompt | null {
  return prompt;
}

/** Call BEFORE opening the hash modal. */
export function setSignInPrompt(next: SignInPrompt): void {
  prompt = next;
  notify();
}

/** The modal clears the prompt when it closes. */
export function clearSignInPrompt(): void {
  if (prompt === null) return;
  prompt = null;
  notify();
}

export function useSignInPrompt(): SignInPrompt | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
